import React, { useState, useEffect } from 'react';
import ProductCard from './ProductCard';
import ProductCarousel from './ProductCarousel';
import CTABox from '../common/CTABox';
import HowItWorks from '../common/HowItWorks';
import Pagination from '../common/Pagination';
import HeroChatbot from '../search/HeroChatbot';
import Seo from '../common/Seo';
import { ShoppingBag, MapPin, Leaf, Search, MessageCircle, X } from 'lucide-react';
import { getProducts, DbProduct } from '../../utils/api';

interface ProductListProps {
  showHero?: boolean;
}

const CATEGORIES = ['Alle', 'Männer', 'Frauen', 'Jungen', 'Mädchen', 'Equipment'];
const PRODUCTS_PER_PAGE = 12;

const ProductList = ({ showHero = true }: ProductListProps) => {
  const [products, setProducts] = useState<DbProduct[]>([]);
  const [searchClub, setSearchClub] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Pagination (nur für /products Route)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchText, setSearchText] = useState('');
  // Mobile: einfache Textsuche + Bot-Modal
  const [mobileSearchText, setMobileSearchText] = useState('');
  const [botModalOpen, setBotModalOpen] = useState(false);
  const [botProducts, setBotProducts] = useState<DbProduct[]>([]);
  const [botHasSearched, setBotHasSearched] = useState(false);

  // Startseite: alle Produkte laden (kein Pagination, für Karussell)
  useEffect(() => {
    if (!showHero) return;
    const loadProducts = async () => {
      try {
        setLoading(true);
        const response = await getProducts();
        if (response.success && response.data) {
          setProducts(response.data);
        } else {
          setError(response.message);
        }
      } catch {
        setError('Fehler beim Laden der Produkte');
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [showHero]);

  // /products Route: paginiert + serverseitig gefiltert
  useEffect(() => {
    if (showHero) return;
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: currentPage.toString(),
          limit: PRODUCTS_PER_PAGE.toString()
        });
        if (selectedCategory) params.append('category', selectedCategory);
        if (searchClub.trim()) params.append('club', searchClub.trim());
        if (searchText.trim()) params.append('search', searchText.trim());

        const res = await fetch(`/api/products.php?${params.toString()}`);
        const data = await res.json();
        if (data.success) {
          setProducts(data.data);
          if (data.pagination) {
            setTotalPages(data.pagination.total_pages);
            setTotalProducts(data.pagination.total_products);
          }
        } else {
          setError(data.message || 'Fehler beim Laden der Produkte');
        }
      } catch {
        setError('Fehler beim Laden der Produkte');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [showHero, currentPage, selectedCategory, searchClub, searchText]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat === 'Alle' ? '' : cat);
    setCurrentPage(1);
  };

  const handleSearchClubChange = (value: string) => {
    setSearchClub(value);
    setCurrentPage(1);
  };

  // Startseite: clientseitig nach Kategorie + mobiler Textsuche filtern
  const filteredProducts = showHero
    ? products.filter(p => {
        if (selectedCategory && p.category !== selectedCategory) return false;
        if (mobileSearchText.trim()) {
          const q = mobileSearchText.trim().toLowerCase();
          return (
            p.title.toLowerCase().includes(q) ||
            (p.description || '').toLowerCase().includes(q) ||
            (p.club || '').toLowerCase().includes(q)
          );
        }
        return true;
      })
    : products;

  return (
    <>
    {showHero ? (
      <Seo
        title="SportyReplay – Gebrauchte Sportartikel im Verein kaufen & verkaufen"
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'SportyReplay',
          url: 'https://sportyreplay.de',
          logo: 'https://sportyreplay.de/favicon-512x512.png',
          description:
            'Marktplatz für gebrauchte Sportartikel innerhalb von Vereinen – regional, nachhaltig und günstig.',
        }}
      />
    ) : (
      <Seo
        title="Alle Sportartikel – gebraucht & günstig | SportyReplay"
        description="Stöbere durch alle gebrauchten Sportartikel bei SportyReplay – nach Verein und Kategorie filtern, regional und nachhaltig kaufen."
        path="/products"
      />
    )}
    <div className="container mx-auto px-2 sm:px-4">
      {showHero ? (
        <>
          {/* Desktop: KI-Bot Banner */}
          <div className="hidden md:block">
            <HeroChatbot />
          </div>

          {/* Mobile: klassische Suchleiste */}
          <div className="md:hidden sticky top-0 z-10 bg-gray-50 py-2 -mx-2 px-2">
            <div className="relative flex items-center">
              <Search className="absolute left-3 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={mobileSearchText}
                onChange={e => setMobileSearchText(e.target.value)}
                placeholder="Artikel, Verein oder Größe suchen..."
                style={{ fontSize: '16px' }}
                className="w-full pl-9 pr-9 py-2 rounded-xl bg-white border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 shadow-sm placeholder-gray-400"
              />
              {mobileSearchText ? (
                <button
                  onClick={() => setMobileSearchText('')}
                  className="absolute right-3 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : null}
            </div>
          </div>

          {/* Filter-Chips */}
          <div className="flex flex-wrap gap-1 md:gap-3 mb-2 md:mb-8">
            {CATEGORIES.map((cat) => {
              const isActive = cat === 'Alle' ? selectedCategory === '' : selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`md:px-6 md:py-3 md:text-base rounded-full font-semibold transition-all transform ${
                    isActive
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={{ padding: '4px 10px', fontSize: '12px' }}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          {/* Feature Cards — nur auf Desktop */}
          <div className="hidden md:grid grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-5 md:p-6 rounded-2xl shadow-md hover:shadow-2xl transition-all transform hover:-translate-y-2 flex md:block items-center gap-4">
              <div className="flex-shrink-0 md:mb-4 md:flex md:justify-center">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl shadow-lg flex items-center justify-center">
                  <MapPin className="text-white w-6 h-6 md:w-7 md:h-7" />
                </div>
              </div>
              <div>
                <h3 className="text-base md:text-xl font-bold mb-1 md:mb-2 text-gray-800 md:text-center">Regional</h3>
                <p className="text-sm md:text-base text-gray-600 md:text-center">
                  Unterstütze deine Vereinsgemeinschaft: Kaufe und verkaufe direkt in deiner Region.
                </p>
              </div>
            </div>

            <div className="bg-white p-5 md:p-6 rounded-2xl shadow-md hover:shadow-2xl transition-all transform hover:-translate-y-2 flex md:block items-center gap-4">
              <div className="flex-shrink-0 md:mb-4 md:flex md:justify-center">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl shadow-lg flex items-center justify-center">
                  <Leaf className="text-white w-6 h-6 md:w-7 md:h-7" />
                </div>
              </div>
              <div>
                <h3 className="text-base md:text-xl font-bold mb-1 md:mb-2 text-gray-800 md:text-center">Nachhaltig</h3>
                <p className="text-sm md:text-base text-gray-600 md:text-center">
                  Gib gebrauchten Sportartikeln ein zweites Leben und trage zu einer nachhaltigeren Welt bei.
                </p>
              </div>
            </div>

            <div className="bg-white p-5 md:p-6 rounded-2xl shadow-md hover:shadow-2xl transition-all transform hover:-translate-y-2 flex md:block items-center gap-4">
              <div className="flex-shrink-0 md:mb-4 md:flex md:justify-center">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl shadow-lg flex items-center justify-center">
                  <ShoppingBag className="text-white w-6 h-6 md:w-7 md:h-7" />
                </div>
              </div>
              <div>
                <h3 className="text-base md:text-xl font-bold mb-1 md:mb-2 text-gray-800 md:text-center">Günstig</h3>
                <p className="text-sm md:text-base text-gray-600 md:text-center">
                  Finde gebrauchte Sportbekleidung und Ausrüstung zu unschlagbaren Preisen.
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        // Alternative Filteransicht für die /products Route
        <div className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 rounded-2xl p-6 sm:p-8 mb-6 mt-4 overflow-hidden shadow-2xl">
          <div className="absolute inset-0 opacity-10 animate-pulse-slow">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <defs>
                <pattern id="grid2" width="10" height="10" patternUnits="userSpaceOnUse">
                  <circle cx="5" cy="5" r="1" fill="white"/>
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#grid2)"/>
            </svg>
          </div>
          <div className="relative z-10 max-w-2xl mx-auto flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              placeholder="Artikel suchen..."
              className="flex-1 p-3 rounded-xl border-0 bg-white text-gray-800 placeholder-gray-500 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-white shadow-lg"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
            />
            <input
              type="text"
              placeholder="Vereinsname..."
              className="flex-1 p-3 rounded-xl border-0 bg-white text-gray-800 placeholder-gray-500 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-white shadow-lg"
              value={searchClub}
              onChange={(e) => handleSearchClubChange(e.target.value)}
            />
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => {
                const isActive = cat === 'Alle' ? selectedCategory === '' : selectedCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => handleCategoryChange(cat)}
                    className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                      isActive
                        ? 'bg-white text-orange-600 shadow-lg'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <>
          {loading && (
            <div className="text-center py-8 sm:py-12">
              <p className="text-gray-600">Lade Produkte...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-8 sm:py-12">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && (
            <>
              {showHero ? (
                /* Startseite: Karussell + CTA */
                <>
                  {botHasSearched && !botModalOpen ? (
                    /* Bot-Ergebnisse nach Modal-Schließen anzeigen */
                    botProducts.length > 0 ? (
                      <>
                        <div className="flex items-center justify-between mb-3 px-1">
                          <p className="text-sm font-semibold text-gray-700">
                            🤖 {botProducts.length} {botProducts.length === 1 ? 'Produkt' : 'Produkte'} vom KI-Assistenten
                          </p>
                          <button
                            onClick={() => { setBotProducts([]); setBotHasSearched(false); }}
                            className="text-xs text-orange-600 font-medium"
                          >
                            Zurücksetzen
                          </button>
                        </div>
                        <ProductCarousel products={botProducts} />
                      </>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-2xl border border-gray-200 mx-1">
                        <div className="text-3xl mb-2">🔍</div>
                        <p className="text-gray-700 font-semibold text-sm">Keine passenden Produkte gefunden.</p>
                        <button
                          onClick={() => { setBotHasSearched(false); setBotModalOpen(true); }}
                          className="mt-3 text-sm text-orange-600 font-medium"
                        >
                          Erneut suchen
                        </button>
                      </div>
                    )
                  ) : filteredProducts.length > 0 ? (
                    <ProductCarousel products={filteredProducts} />
                  ) : mobileSearchText.trim() ? (
                    /* Mobile: Suche ohne Treffer → Bot-Hinweis */
                    <div className="text-center py-8 bg-gray-50 rounded-2xl border border-gray-200 mx-1">
                      <div className="text-3xl mb-2">🔍</div>
                      <p className="text-gray-700 font-semibold text-sm">Keine Artikel gefunden.</p>
                      <p className="text-gray-500 text-xs mt-1 mb-4 px-4">
                        Nichts gefunden? Vielleicht hat ein anderer Verein was du suchst –
                        frag einfach den 🤖 KI-Assistenten!
                      </p>
                      <button
                        onClick={() => setBotModalOpen(true)}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-xl font-semibold text-sm transition-colors shadow-md"
                      >
                        KI-Assistenten fragen
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-8 sm:py-12">
                      <p className="text-gray-600 text-sm sm:text-lg">Keine Produkte gefunden.</p>
                    </div>
                  )}
                  <CTABox />
                  <HowItWorks />
                </>
              ) : (
                /* /products Seite: Grid + Pagination */
                <>
                  <div className="flex justify-between items-center mb-4 sm:mb-6">
                    <h2 className="text-xl sm:text-2xl font-bold">
                      {`${totalProducts} ${totalProducts === 1 ? 'Produkt' : 'Produkte'} gefunden`}
                    </h2>
                  </div>

                  {products.length > 0 ? (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
                        {products.map(product => (
                          <ProductCard key={product.id} {...product} />
                        ))}
                      </div>

                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        className="mt-12"
                      />

                      {totalProducts > 0 && (
                        <p className="text-center text-gray-500 text-sm mt-4">
                          Zeige {((currentPage - 1) * PRODUCTS_PER_PAGE) + 1}–{Math.min(currentPage * PRODUCTS_PER_PAGE, totalProducts)} von {totalProducts} Produkten
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 sm:py-12">
                      <p className="text-gray-600 text-sm sm:text-lg">Keine Produkte gefunden.</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
    </div>

      {/* Floating Bot Button — Mobile only, nur auf Startseite */}
      {showHero && (
        <button
          data-testid="floating-bot-button"
          onClick={() => setBotModalOpen(true)}
          className="md:hidden fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full bg-orange-500 shadow-xl flex items-center justify-center active:scale-95 transition-transform"
          aria-label="KI-Assistent öffnen"
        >
          <MessageCircle className="w-7 h-7 text-white" />
        </button>
      )}

      {/* Bot-Modal (Slide-up Sheet, 75% Höhe) */}
      {showHero && botModalOpen && (
        <div
          className="md:hidden fixed inset-0 z-[60] flex flex-col justify-end"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={e => { if (e.target === e.currentTarget) setBotModalOpen(false); }}
        >
          <div className="mx-4 rounded-3xl overflow-hidden" style={{ height: '75vh' }}>
            <HeroChatbot
              asModal={true}
              onClose={() => setBotModalOpen(false)}
              onSearchComplete={(prods, searched) => { setBotProducts(prods); setBotHasSearched(searched); }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ProductList;
